package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.ui.components.IconBackspace
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith

private val KeyboardRows = listOf(
    "QWERTYUIOP",
    "ASDFGHJKL",
    "ZXCVBNM",
)

/**
 * On-screen letter keyboard — the full 26 keys.
 *
 * Always available — a physical keyboard is already wired up on desktop and web
 * through the screen's key handler, so this is the touch path and a mouse
 * fallback rather than an either/or. Keys use the same press-travel language as
 * [er.prateek.crossly.ui.components.GameButton] at a smaller scale, and shrink
 * with [keyHeight] when vertical space is tight.
 *
 * [LetterRackBar] is the trimmed alternative, chosen in Settings.
 */
@Composable
fun KeyboardBar(
    onLetter: (Char) -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier,
    keyHeight: Dp = 46.dp,
    dragState: LetterDragState? = null,
    onDropOnCell: (CellPos, Char) -> Unit = { _, _ -> },
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        KeyboardRows.forEachIndexed { index, row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                // Half-key indents on the lower rows reproduce the stagger of a
                // real keyboard, which makes the layout readable at a glance.
                if (index > 0) Box(modifier = Modifier.weight(index * 0.5f))
                row.forEach { letter ->
                    GameKey(
                        // Keys drag onto the board exactly like rack tiles do, so
                        // the gesture doesn't disappear when a player switches to
                        // the full keyboard.
                        modifier = Modifier
                            .weight(1f)
                            .draggableLetter(letter, dragState, onDropped = onDropOnCell),
                        height = keyHeight,
                        onClick = { onLetter(letter) },
                    ) { tint ->
                        Text(
                            text = letter.toString(),
                            style = MaterialTheme.typography.titleMedium,
                            color = tint,
                        )
                    }
                }
                if (index == KeyboardRows.lastIndex) {
                    GameKey(
                        modifier = Modifier.weight(2f),
                        height = keyHeight,
                        emphasized = true,
                        onClick = onDelete,
                    ) { tint ->
                        IconBackspace(size = 20.dp, tint = tint)
                    }
                } else if (index > 0) {
                    Box(modifier = Modifier.weight(index * 0.5f))
                }
            }
        }
    }
}

/**
 * One extruded key face, shared by the full keyboard and the letter rack: a
 * solid shadow layer with the face travelling down onto it when pressed.
 *
 * [enabled] false is the rack's "already used up" state — the key keeps its slot
 * (so nothing reflows under the player's thumb) but goes flat and inert.
 */
@Composable
internal fun GameKey(
    modifier: Modifier = Modifier,
    height: Dp,
    emphasized: Boolean = false,
    enabled: Boolean = true,
    onClick: () -> Unit,
    content: @Composable (Color) -> Unit,
) {
    val colors = MaterialTheme.game
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val hovered by interactionSource.collectIsHoveredAsState()
    val depth = 3.dp
    val travel by animateDpAsState(
        targetValue = if (pressed && enabled) depth else 0.dp,
        animationSpec = spring(dampingRatio = 0.5f, stiffness = 1800f),
        label = "keyTravel",
    )

    val shape = RoundedCornerShape(10.dp)
    val face = when {
        !enabled -> colors.cardElevated.mixWith(colors.inkMuted, 0.18f).copy(alpha = 0.5f)
        emphasized -> colors.accent.copy(alpha = 0.16f)
        hovered -> colors.cardElevated.mixWith(colors.accent, 0.12f)
        else -> colors.cardElevated
    }
    val tint = when {
        !enabled -> colors.inkMuted.copy(alpha = 0.5f)
        emphasized -> colors.accent
        else -> colors.ink
    }

    Box(modifier = modifier.padding(bottom = depth)) {
        if (enabled) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .offset(y = depth)
                    .clip(shape)
                    .background(colors.shadow),
            )
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(height)
                .offset(y = travel)
                .clip(shape)
                .background(face)
                .border(1.dp, colors.cardBorder, shape)
                .hoverable(interactionSource)
                .clickable(
                    interactionSource = interactionSource,
                    indication = null,
                    enabled = enabled,
                    onClick = onClick,
                ),
            contentAlignment = Alignment.Center,
        ) {
            content(tint)
        }
    }
}
